import { Resolver, useForm } from 'react-hook-form';
import {
    ApplyNowFormValues,
    applyNowSchema,
    emptyApplyNowForm,
    IApplyNowForm,
    UploadFileTypes,
} from '../../common';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import {
    Form,
    FormCheckbox,
    FormInputText,
    FormSelector,
    FormTextArea,
    FormUploadFile,
    Grid,
    PargodaRoundButton,
    Stack,
    Typography,
    useAppDispatch,
    useAppSelector,
    useTheme,
} from '../../base';
import {
    IApplyToJobAction,
    jobApplicationActions,
    jobApplicationSelectors,
    jobsActions,
    jobsSelectors,
    userSelectors,
} from '../../actions';
import { useLocation, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import CandidateForm from '../CandidateForm';
import {
    StyledApplyNowBox,
    StyledApplyNowMiddleContainerBox,
} from './ApplyNowModal.styles';
import PargodaModal from '../PargodaModal';
import TermsAndConditionsCheckbox from '../TermsAndConditionsCheckbox';

interface Props {
    positionTitle?: string;
    isModalOpen: boolean;
    setIsModalOpen: (isModalOpen: boolean) => void;
    isEmployerPortal?: boolean;
}
const ApplyNowModal = ({
    positionTitle,
    isModalOpen,
    setIsModalOpen,
    isEmployerPortal = false,
}: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const { id = '0' } = useParams();
    const theme = useTheme();

    const methods = useForm<IApplyNowForm>({
        resolver: yupResolver(
            applyNowSchema(t, isEmployerPortal)
        ) as Resolver<IApplyNowForm>,
        mode: 'onSubmit',
        defaultValues: emptyApplyNowForm,
    });

    const { watch, reset, control, getValues } = methods;

    const isApplyLoadingOrSubmitting = useAppSelector(
        jobApplicationSelectors.getApplyNowLoading
    );

    const companyInfo = useAppSelector(userSelectors.getCompanyInfo);

    const publishedJobsOptions = useAppSelector(
        jobsSelectors.getPublishedJobsOptions
    );

    const isReferrer = watch(ApplyNowFormValues.isReferrer);

    useEffect(() => {
        if (isEmployerPortal) {
            dispatch(jobsActions.fetchPublishedJobsOptions());
        }
    }, [dispatch, isEmployerPortal]);

    useEffect(() => {
        if (isModalOpen) {
            reset(emptyApplyNowForm);
        }
    }, [isModalOpen]);

    const applyNowSubmit = () => {
        const queryParams = new URLSearchParams(location.search);
        const companyKey = queryParams.get('ck') || companyInfo?.key || '';
        const ref = queryParams.get('ref');
        const formData = getValues();
        const applyNowActionData: IApplyToJobAction = {
            jobId: isEmployerPortal
                ? formData[ApplyNowFormValues.jobApplicationId]?.value ?? ''
                : id,
            payload: {
                first_name: formData[ApplyNowFormValues.firstName],
                last_name: formData[ApplyNowFormValues.lastName],
                phone: formData[ApplyNowFormValues.phoneNumber],
                email: formData[ApplyNowFormValues.email],
                resume: {
                    file: formData[ApplyNowFormValues.file],
                    accepted_terms: formData[ApplyNowFormValues.acceptTerms],
                },
                notes: formData[ApplyNowFormValues.coverLetter] || undefined,
                ref: ref || undefined,
                company_key: companyKey,
                referrer: formData[ApplyNowFormValues.isReferrer]
                    ? {
                          first_name:
                              formData[ApplyNowFormValues.referrerName] ?? '',
                          last_name:
                              formData[ApplyNowFormValues.referrerLastName] ??
                              '',
                          phone:
                              formData[
                                  ApplyNowFormValues.referrerPhoneNumber
                              ] ?? '',
                          company_name:
                              formData[ApplyNowFormValues.referrerCompany] ??
                              '',
                          email:
                              formData[ApplyNowFormValues.referrerEmail] ?? '',
                          is_point_of_contact: true,
                      }
                    : undefined,
            },
        };
        dispatch(jobApplicationActions.applyToJob(applyNowActionData));
    };

    return (
        <PargodaModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            sx={{
                width: '595px',
                maxWidth: '100%',
                padding: theme.spacing(3.5),
                height: '97%',
            }}
        >
            <Form methods={methods} onSubmit={applyNowSubmit}>
                <StyledApplyNowBox>
                    <Grid container>
                        <Grid item xs={12} pb={2.25}>
                            <Typography
                                variant={'h6'}
                                color={theme.palette.grey[600]}
                            >
                                {isEmployerPortal
                                    ? t('applyNow.titleManual')
                                    : t('applyNow.title', {
                                          positionTitle: positionTitle,
                                          interpolation: { escapeValue: false }, // Disable escaping
                                      })}
                            </Typography>
                        </Grid>
                    </Grid>
                    <StyledApplyNowMiddleContainerBox>
                        <Grid
                            container
                            spacing={1}
                            minHeight="min-content"
                            width="100%"
                        >
                            <Grid item xs={12} pb={1}>
                                <Typography variant={'body1'}>
                                    {t('applyNow.candidateInfoTitle')}
                                </Typography>
                            </Grid>
                            {isEmployerPortal && (
                                <Grid item xs={12}>
                                    <FormSelector
                                        name={
                                            ApplyNowFormValues.jobApplicationId
                                        }
                                        control={control}
                                        options={publishedJobsOptions}
                                        label={t('applyNow.selectAPosition')}
                                    />
                                </Grid>
                            )}
                            <CandidateForm
                                control={control}
                                name={ApplyNowFormValues.firstName}
                                lastName={ApplyNowFormValues.lastName}
                                phoneNumber={ApplyNowFormValues.phoneNumber}
                                email={ApplyNowFormValues.email}
                            />
                            <Grid item xs={12}>
                                <FormCheckbox
                                    name={ApplyNowFormValues.isReferrer}
                                    control={control}
                                    label={
                                        <Stack direction="row">
                                            <Typography variant="body2">
                                                {t('applyNow.isReferrer')}
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            </Grid>
                            {isReferrer && (
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={12} pt={1} pb={2}>
                                            <Typography variant={'body1'}>
                                                {t(
                                                    'applyNow.referrerInfoTitle'
                                                )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container spacing={1}>
                                                <CandidateForm
                                                    control={control}
                                                    name={
                                                        ApplyNowFormValues.referrerName
                                                    }
                                                    lastName={
                                                        ApplyNowFormValues.referrerLastName
                                                    }
                                                    phoneNumber={
                                                        ApplyNowFormValues.referrerPhoneNumber
                                                    }
                                                    email={
                                                        ApplyNowFormValues.referrerEmail
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} pt={1.4}>
                                            <FormInputText
                                                name={
                                                    ApplyNowFormValues.referrerCompany
                                                }
                                                control={control}
                                                label={t(
                                                    'applyNow.referrerCompany'
                                                )}
                                                required
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Typography variant={'h6'}>
                                    {t('applyNow.uploadResume')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} pb={3}>
                                <Typography
                                    variant={'caption'}
                                    color={theme.palette.grey[600]}
                                >
                                    {t('applyNow.pleaseUploadResume')}
                                </Typography>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                display={'flex'}
                                justifyContent={'center'}
                                pb={7}
                            >
                                <FormUploadFile
                                    name={ApplyNowFormValues.file}
                                    type={UploadFileTypes.file}
                                    sx={{
                                        height: '140px',
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant={'h6'}>
                                    {t('applyNow.additionalInformation')}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <FormTextArea
                                    name={ApplyNowFormValues.coverLetter}
                                    control={control}
                                    label={t('applyNow.additionalInformation')}
                                    rows={4}
                                />
                            </Grid>
                        </Grid>
                    </StyledApplyNowMiddleContainerBox>
                    <Grid container>
                        <Grid item xs={12}>
                            <Grid container>
                                <Grid
                                    item
                                    xs={12}
                                    display={'flex'}
                                    justifyContent={'center'}
                                    pb={2}
                                    pt={2}
                                >
                                    <TermsAndConditionsCheckbox
                                        name={ApplyNowFormValues.acceptTerms}
                                        control={control}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={6}
                                    display={'flex'}
                                    justifyContent={'flex-end'}
                                    pb={2}
                                >
                                    <PargodaRoundButton
                                        textColor={theme.palette.grey[600]}
                                        variant={'outlined'}
                                        loadingPosition={'start'}
                                        onClick={() => {
                                            setIsModalOpen(false);
                                        }}
                                        startIcon={<></>}
                                        loading={isApplyLoadingOrSubmitting}
                                        sx={{
                                            marginInlineEnd: theme.spacing(1.5),
                                            height: '48px',
                                            width: '214px',
                                            fontWeight: 600,
                                            fontSize: '16px',
                                        }}
                                    >
                                        {t('applyNow.applyNowCancelButton')}
                                    </PargodaRoundButton>
                                </Grid>
                                <Grid item xs={6} display={'flex'} pb={2}>
                                    <PargodaRoundButton
                                        textColor={theme.palette.common.white}
                                        variant={'contained'}
                                        type={'submit'}
                                        loadingPosition={'start'}
                                        startIcon={<></>}
                                        loading={isApplyLoadingOrSubmitting}
                                        sx={{
                                            background: `linear-gradient(0deg,${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                            width: '214px',
                                            fontWeight: 600,
                                            fontSize: '16px',
                                        }}
                                    >
                                        {t('applyNow.applyNowButton')}
                                    </PargodaRoundButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledApplyNowBox>
            </Form>
        </PargodaModal>
    );
};

export default ApplyNowModal;
